import { startCacheMode } from "@newxsystems/platform-contracts/current/cache";
import {
  hasNoStoreCacheDirective,
  stripCacheDirectiveTags,
  type IPageCachePolicy,
  type IPageMetadata,
} from "@newxsystems/types";

const DEFAULT_SHARED_CACHE_SECONDS = 300;
const DEFAULT_STALE_WHILE_REVALIDATE_SECONDS = 86_400;
const DEFAULT_STALE_IF_ERROR_SECONDS = 86_400;
const STATIC_SHARED_CACHE_SECONDS = 31_536_000;

export type StartCacheHeaderInput = {
  cache?: IPageCachePolicy;
  isPreview?: boolean;
  path?: string;
  statusCode?: number;
  tags?: string[];
};

export type StartCacheHeaders = {
  headers: Record<string, string>;
  mode: "cacheable" | "no-store";
  path?: string;
  tags: string[];
};

const numberFromEnv = (names: string[], fallback: number) => {
  for (const name of names) {
    const value = process.env[name];
    if (!value) continue;

    const parsed = Number.parseInt(value, 10);
    if (Number.isFinite(parsed) && parsed >= 0) {
      return parsed;
    }
  }

  return fallback;
};

export const normalizeCachePath = (path: unknown) => {
  if (typeof path !== "string") return undefined;
  const trimmed = path.trim();
  if (!trimmed) return undefined;
  return trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
};

export const normalizeCacheTag = (tag: unknown) => {
  if (typeof tag !== "string") return undefined;
  const trimmed = tag.trim();
  if (!trimmed || trimmed.length > 1_024 || !/^[\x21-\x7e]+$/.test(trimmed)) return undefined;
  return trimmed;
};

export const normalizeCachePaths = (paths: unknown[] | undefined) =>
  [...new Set((paths ?? []).map(normalizeCachePath).filter(Boolean))] as string[];

export const normalizeCacheTags = (tags: unknown[] | undefined) =>
  [...new Set((tags ?? []).map(normalizeCacheTag).filter(Boolean))] as string[];

const mergeCacheTags = (...tagGroups: Array<unknown[] | undefined>) =>
  normalizeCacheTags(tagGroups.flatMap((tags) => tags ?? []));

const getSharedCacheSeconds = (cache: IPageCachePolicy | undefined) => {
  if (typeof cache?.revalidateSeconds === "number") {
    return Math.max(0, cache.revalidateSeconds);
  }

  if (cache?.revalidateSeconds === false) {
    return STATIC_SHARED_CACHE_SECONDS;
  }

  return numberFromEnv(
    ["START_CMS_S_MAXAGE_SECONDS", "CMS_S_MAXAGE_SECONDS"],
    DEFAULT_SHARED_CACHE_SECONDS,
  );
};

const getStaleWhileRevalidateSeconds = (cache: IPageCachePolicy | undefined) =>
  cache?.revalidateSeconds === false
    ? 0
    : numberFromEnv(
        ["START_CMS_STALE_WHILE_REVALIDATE_SECONDS", "CMS_STALE_WHILE_REVALIDATE_SECONDS"],
        DEFAULT_STALE_WHILE_REVALIDATE_SECONDS,
      );

const getStaleIfErrorSeconds = () =>
  numberFromEnv(
    ["START_CMS_STALE_IF_ERROR_SECONDS", "CMS_STALE_IF_ERROR_SECONDS"],
    DEFAULT_STALE_IF_ERROR_SECONDS,
  );

const shouldNoStore = ({ cache, isPreview, statusCode, tags }: StartCacheHeaderInput) => {
  if (startCacheMode === "no-store") return true;
  if (isPreview) return true;
  if (statusCode && statusCode >= 400) return true;
  if (hasNoStoreCacheDirective(mergeCacheTags(cache?.tags, tags))) return true;
  return cache?.mode === "dynamic" || cache?.mode === "no-store";
};

export const createNoStoreCacheHeaders = (path?: string): StartCacheHeaders => ({
  headers: {
    "Cache-Control": "no-store",
    "CDN-Cache-Control": "no-store",
    "Cloudflare-CDN-Cache-Control": "no-store",
    ...(path ? { "X-Cache-Path": path } : {}),
  },
  mode: "no-store",
  path,
  tags: [],
});

export const createStartCacheHeaders = (input: StartCacheHeaderInput): StartCacheHeaders => {
  const path = normalizeCachePath(input.path);

  if (shouldNoStore(input)) {
    return createNoStoreCacheHeaders(path);
  }

  const tags = mergeCacheTags(input.cache?.tags, input.tags, path ? [`page:${path}`] : undefined);
  const emittedTags = stripCacheDirectiveTags(tags).slice(0, 1_000);
  const sharedCacheSeconds = getSharedCacheSeconds(input.cache);
  const staleWhileRevalidateSeconds = getStaleWhileRevalidateSeconds(input.cache);
  const staleIfErrorSeconds = getStaleIfErrorSeconds();
  const sharedDirectives = [
    "public",
    `s-maxage=${sharedCacheSeconds}`,
    ...(staleWhileRevalidateSeconds > 0
      ? [`stale-while-revalidate=${staleWhileRevalidateSeconds}`]
      : []),
    ...(staleIfErrorSeconds > 0 ? [`stale-if-error=${staleIfErrorSeconds}`] : []),
  ];
  const cdnCacheControl = sharedDirectives.join(", ");
  const headers: Record<string, string> = {
    "Cache-Control": ["public", "max-age=0", ...sharedDirectives.slice(1)].join(", "),
    "CDN-Cache-Control": cdnCacheControl,
    "Cloudflare-CDN-Cache-Control": cdnCacheControl,
  };

  if (path) {
    headers["X-Cache-Path"] = path;
  }

  if (emittedTags.length) {
    const serializedTags = emittedTags.join(",");
    headers["Cache-Tag"] = serializedTags;
    headers["X-Cache-Tags"] = serializedTags;
  }

  return {
    headers,
    mode: "cacheable",
    path,
    tags: emittedTags,
  };
};

export const createPageCacheHeaders = ({
  metadata,
  ...input
}: StartCacheHeaderInput & { metadata?: IPageMetadata }): StartCacheHeaders =>
  createStartCacheHeaders({
    cache: input.cache ?? metadata?.cache,
    isPreview: input.isPreview,
    path: input.path ?? metadata?.identity.path,
    statusCode: input.statusCode,
    tags: input.tags,
  });
